import { create } from "zustand";
import {
  applyCommentAdd,
  applyCommentDelete,
  applyCommentUpdate,
  isDrawableObject,
  opMayAffectExportSnapshot,
  type CanvasObject,
  type CanvasOp,
  type CanvasSizePreset,
  type TransformObjectOp,
} from "@refinement-canvas/domain";
import { DEFAULT_CANVAS_TITLE } from "../../../lib/canvas-title";
import {
  PENDING_ATTACHMENT_SRC,
  isPendingAttachmentSrc,
} from "../attachment-placeholder";

// キャンバスのドキュメント状態（メタ情報とオブジェクト一覧）を管理する Zustand store。
// サーバ権威のスナップショットと、自分が送ったがまだ ack されていない inFlightOps を分離保持する。

export interface CanvasMetaState {
  readonly title: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly thumbnailKey?: string | undefined;
  readonly thumbnailUrl?: string | undefined;
  /**
   * 全体像 PDF スナップショットの S3 キー（Connect Note 連携。公開 URL は持たない）。
   * @internal 参考情報のみ。bootstrap / メタ更新応答は META の素通しで返すため、この値があっても全体像が
   * 「実体ありかつ非空」であることは保証しない（drawable を全削除すると、サーバの export-url は exportKey が
   * 残っていても 404 を返す）。取得可否は常に export-url の結果で判断し、この値を UI のリンク活性・
   * 「全体像あり」表示の指標として使ってはならない。
   */
  readonly exportKey?: string | undefined;
  /**
   * このキャンバスを、現在のログインユーザーがお気に入り登録済みか。
   * 共同編集の対象ではない個人単位の状態で、ブートストラップで seed し、星ボタンのトグルで更新する。
   */
  readonly isFavorite?: boolean | undefined;
  /**
   * キャンバスサイズのプリセット（小 / 中 / 大）。サーバ権威で共有される共同編集対象の状態。
   * 未設定は既定=中(10K) として扱う（描画・書き出し側は normalizeCanvasSizePreset で解決する）。
   */
  readonly sizePreset?: CanvasSizePreset | undefined;
}

interface CanvasDocState {
  canvasId: string | null;
  /**
   * committed へ「連続して」反映済みの最大 serverSeq（＝適用済み水位）。committedObjects/committedMeta は
   * この seq までの op をサーバの採番順どおり反映している。stale snapshot 判定にも使う。
   */
  serverSeq: number;
  /**
   * まだ committed へ反映していない、順序が飛んで先に届いた op.commit を serverSeq をキーに保持するバッファ。
   * 一括移動では複数 op が個別に採番され、broadcast 配信で順不同に届くため、間の seq が未着なら一旦ここへ積み、
   * 連続が埋まり次第まとめて committed へ畳み込む（applyOpToObjects を厳密な serverSeq 昇順で適用する
   * ＝サーバのイベントログを正しい順序で再生する）。これにより次を同時に満たす:
   *  - 相異なるオブジェクトの確定を順不同でも取りこぼさない（一括移動で図形が元位置へ巻き戻らない）。
   *  - 同一オブジェクトへの部分更新・増分更新（transform の x/y 別フィールド、コメント追記など）を、
   *    順不同到着でも取りこぼさず、最終状態をサーバと一致させる。
   * 自分の未 ack op はバッファ待ちの間も inFlightOps に残るため、確定待ちでも楽観表示のまま（巻き戻らない）。
   * loadSnapshot でリセットする（スナップショットが committed を作り直し、連続水位 serverSeq を張り直すため）。
   */
  pendingCommits: Record<number, CanvasOp>;
  /**
   * 恒久欠番によるバッファ上限フラッシュ（flushPendingCommits がバッファを破棄・水位据え置き）と、
   * 初回ロード中（canvasId=null）に stale bootstrap を無視したとき（loadSnapshot の stale-null 分岐。
   * committed が権威スナップショット未取り込みの乖離状態になり得るため）に increment される単調増加
   * カウンタ。use-realtime-connection がこの increment を検知し、REST bootstrap で権威スナップショットへ
   * resync する（いずれもスナップショットの取り直しでしか正しく回復できないため。通常経路では増えない）。
   */
  resyncRequestSeq: number;
  /**
   * 権威スナップショットの再取得（resync）待ちの間 true。resyncRequestSeq を進める両経路——初回ロード中
   * （canvasId=null）に stale bootstrap を無視したとき（loadSnapshot の stale-null 分岐）と、恒久欠番で
   * バッファを破棄したとき（flushPendingCommits）——で立ち、再取得の全適用（通常の loadSnapshot）または
   * reset で下りる。true の間、committed / objects は権威スナップショット未取り込みの乖離状態であり得る
   * ため、サムネイル自動保存（useCanvasThumbnail）・全体像 PDF 自動保存（useCanvasExportSnapshot）は
   * このフラグを見て起動を待機し、一時的な乖離状態をサーバへ永続化しないようにする（resync が失敗して
   * も保存停止側に倒れる＝安全側）。
   */
  resyncPending: boolean;
  meta: CanvasMetaState;
  /** 画面表示用（サーバ確定状態に inFlightOps を重ねたもの） */
  objects: Record<string, CanvasObject>;
  /** サーバ確定スナップショット */
  committedMeta: CanvasMetaState;
  committedObjects: Record<string, CanvasObject>;
  /** ローカルで先行適用した op（ack 待ち） */
  inFlightOps: CanvasOp[];
  /**
   * 画像・動画添付の LQIP（低品質プレースホルダ、data: URL）を objectId 単位で保持する。
   * object.create で受け取った data: URL を一度キャプチャしておき、その後の attachment.update で
   * 本体 URL（公開 URL / blob:）へ差し替えられても残し、本体 URL の読み込み中や失敗時に
   * ぼかしプレビューとして表示するためのフォールバックに使う。
   * 透明 1x1 プレースホルダ（PENDING_ATTACHMENT_SRC）は背景として意味がないため除外する。
   * クライアント側ローカルキャッシュであり、サーバへは送らない。
   */
  attachmentLqips: Record<string, string>;
  /**
   * このタブのローカル編集（applyOptimistic）によって、全体像 PDF スナップショットの再保存が必要な状態か。
   * 共同編集では、編集していない受信専用タブ（canWrite を持つが他者編集を realtime で反映しているだけのタブ）
   * でも objects が変化する。これを export 自動保存のトリガにすると、回線の遅い観測者タブや再接続直後のタブが
   * プレースホルダ混じりの劣化 PDF で current を上書きし得る。そこで「ローカル編集で dirty になったときだけ」
   * 保存するよう、ローカル編集（applyOptimistic）で true、サーバ状態のロード（loadSnapshot）と finalize 成功で
   * false にする。受信（onServerCommit）では変更しない（観測者タブは保存しない）。
   */
  exportSnapshotDirty: boolean;
  /**
   * export へ影響するローカル編集（applyOptimistic で opAffectsExportSnapshot が真）の回数を単調増加で数える。
   * 自動保存フックは保存対象を読み取った時点（発火時）の seq を控え、finalize 後に seq が進んでいれば
   * 「保存中に新しい drawable 編集が入った（この PDF に未反映）」と判断して dirty を下げず再保存する。
   * effect 依存から objects を外し、共同編集受信でデバウンスがリセットされる飢餓を避けた分、保存中の
   * 追加編集の取りこぼしをこの seq 照合で防ぐ。リセット時のみ 0 へ戻す（単調性は保存サイクル内で足りる）。
   */
  exportSnapshotDirtySeq: number;

  /**
   * サーバ権威スナップショットを取り込む。返り値は「この取り込みで committed 化した pending op」群
   * （snapshot 包含済み＋連続ドレイン分）。呼び出し側はこの op に対応する transient ゴーストを破棄する
   * （順不同で先着してバッファ保留中だった op が snapshot 経由で確定したのにゴーストが残るのを防ぐ）。
   * stale snapshot（古い serverSeq）で無視した場合は空配列。
   */
  loadSnapshot: (params: {
    canvasId: string;
    serverSeq: number;
    objects: CanvasObject[];
    meta?: CanvasMetaState | undefined;
  }) => readonly CanvasOp[];
  updateLocalMeta: (meta: Partial<CanvasMetaState>) => void;
  applyOptimistic: (op: CanvasOp) => void;
  /**
   * サーバから op.commit を受信したときに呼ぶ。返り値は「今回 committed へ実際に畳み込んだ op」群
   * （serverSeq 昇順）。順不同で先着してバッファ保留しただけの commit は含めない。呼び出し側は
   * この返り値の op に対してのみ transient ゴーストを破棄する（保留中の op のゴーストは残す）。
   */
  onServerCommit: (op: CanvasOp, serverSeq: number) => readonly CanvasOp[];
  /** サーバから op.reject を受信したときに呼ぶ（自分の op が拒否） */
  onServerReject: (clientOpId: string) => void;
  /**
   * 送信せずローカルにだけ適用した op（本人だけのプレビュー上書きなど）を inFlightOps から取り除く。
   * サーバ ack が来ない一時的なローカル op を、表示確定のタイミングで明示的に破棄するために使う。
   */
  removeInFlightOp: (clientOpId: string) => void;
  /**
   * 欠番（pendingCommits に op が滞留したまま水位 serverSeq が進まない状態）が一定時間続いたときに
   * 呼ぶ。恒久欠番（採番済みなのに OP 行が書かれず配信されない等）は後続 op では絶対に埋まらず、
   * 従来は上限 500 件に達するまで全員の確定が UI 通知なしで停止し続けた。バッファを破棄して
   * resyncRequestSeq を進め、権威スナップショットでの resync を能動的に促す。
   * pendingCommits が空（欠番が既に解消済み）なら何もしない。
   */
  requestGapResync: () => void;
  /** 全体像 PDF の保存（finalize）が成功し、現在の状態が export 済みになったときに dirty を下げる。 */
  clearExportSnapshotDirty: () => void;
  /** 接続切断などで完全リセットするとき */
  reset: () => void;
}

const DEFAULT_META: CanvasMetaState = {
  title: DEFAULT_CANVAS_TITLE,
  createdAt: "",
  updatedAt: "",
};

// pendingCommits が恒久的な欠番（配信欠落）で無制限に膨らむのを防ぐ上限。到達したら連続性を諦め、
// バッファを破棄し（適用しない・水位は据え置き）、resyncRequestSeq を進めて権威スナップショットでの
// resync を促す（欠番の効果は resync で収束する）。テストからも境界を参照できるよう export する。
export const PENDING_COMMITS_FLUSH_THRESHOLD = 500;

/**
 * pendingCommits のうち fromSeq の次（fromSeq + 1）から連続する op を committed へ順に畳み込み、適用水位を
 * 進める。サーバの採番順（serverSeq 昇順）どおりに適用するため、同一オブジェクトへの部分更新・増分更新
 * （transform の x/y 別フィールド、コメント追記など）が順不同到着でも正しく合成される。
 * 返り値は畳み込み後の committed / inFlightOps / attachmentLqips、進めた appliedSeq、残ったバッファ。
 */
function drainContiguousPending(input: {
  readonly committedMeta: CanvasMetaState;
  readonly committedObjects: Record<string, CanvasObject>;
  readonly inFlightOps: CanvasOp[];
  readonly attachmentLqips: Record<string, string>;
  readonly fromSeq: number;
  readonly pendingCommits: Record<number, CanvasOp>;
}): {
  committedMeta: CanvasMetaState;
  committedObjects: Record<string, CanvasObject>;
  inFlightOps: CanvasOp[];
  attachmentLqips: Record<string, string>;
  appliedSeq: number;
  pendingCommits: Record<number, CanvasOp>;
  /** committed へ実際に畳み込んだ op 群（serverSeq 昇順）。transient ゴーストの破棄対象に使う。 */
  appliedOps: CanvasOp[];
} {
  let committedMeta = input.committedMeta;
  let committedObjects = input.committedObjects;
  let inFlightOps = input.inFlightOps;
  let attachmentLqips = input.attachmentLqips;
  let appliedSeq = input.fromSeq;
  let pendingCommits = input.pendingCommits;
  let pendingChanged = false;
  const appliedOps: CanvasOp[] = [];
  for (;;) {
    const nextOp = pendingCommits[appliedSeq + 1];
    if (nextOp === undefined) break;
    if (!pendingChanged) {
      pendingCommits = { ...pendingCommits };
      pendingChanged = true;
    }
    delete pendingCommits[appliedSeq + 1];
    const folded = applyOpToState({ meta: committedMeta, objects: committedObjects }, nextOp);
    committedMeta = folded.meta;
    committedObjects = folded.objects;
    inFlightOps = inFlightOps.filter((p) => p.clientOpId !== nextOp.clientOpId);
    attachmentLqips = nextAttachmentLqips(nextOp, attachmentLqips);
    appliedSeq += 1;
    appliedOps.push(nextOp);
  }
  return {
    committedMeta,
    committedObjects,
    inFlightOps,
    attachmentLqips,
    appliedSeq,
    pendingCommits,
    appliedOps,
  };
}

/**
 * 連続する次の serverSeq の op.commit（firstOp, seq = state.serverSeq + 1）を committed へ畳み込み、
 * 後続のバッファ（pendingCommits）を連続する限りドレインする。serverSeq 昇順で適用するため、同一
 * オブジェクトへの部分更新・増分更新が順不同到着でも正しく合成され、最終状態がサーバと一致する。
 */
function applyContiguousCommit(
  state: CanvasDocState,
  firstOp: CanvasOp
): { state: Partial<CanvasDocState>; appliedOps: CanvasOp[] } {
  const firstSeq = state.serverSeq + 1;
  const folded = applyOpToState(
    { meta: state.committedMeta, objects: state.committedObjects },
    firstOp
  );
  const inFlightOps = state.inFlightOps.filter((p) => p.clientOpId !== firstOp.clientOpId);
  const attachmentLqips = nextAttachmentLqips(firstOp, state.attachmentLqips);
  // firstOp と同一 seq がバッファに重複していれば掃除する（broadcast 再配信対策）。
  let pendingCommits = state.pendingCommits;
  if (pendingCommits[firstSeq] !== undefined) {
    pendingCommits = { ...pendingCommits };
    delete pendingCommits[firstSeq];
  }
  const drained = drainContiguousPending({
    committedMeta: folded.meta,
    committedObjects: folded.objects,
    inFlightOps,
    attachmentLqips,
    fromSeq: firstSeq,
    pendingCommits,
  });
  const next = drained.inFlightOps.reduce(applyOpToState, {
    meta: drained.committedMeta,
    objects: drained.committedObjects,
  });
  return {
    state: {
      serverSeq: drained.appliedSeq,
      pendingCommits: drained.pendingCommits,
      committedMeta: drained.committedMeta,
      committedObjects: drained.committedObjects,
      meta: next.meta,
      objects: next.objects,
      inFlightOps: drained.inFlightOps,
      attachmentLqips: drained.attachmentLqips,
    },
    // 実際に committed へ畳み込んだ op（firstOp ＋ ドレインした後続）。transient 破棄対象に返す。
    appliedOps: [firstOp, ...drained.appliedOps],
  };
}

/**
 * 欠番でバッファが上限に達したときの回収。順不同で先着した op 群は連続化できず、正しく確定できない
 * （欠番を飛ばして順不同適用すると同一オブジェクトの順序逆転を招き、水位 serverSeq を欠番越えに進めると
 * 後から欠番 seq が遅れて到着したときに onServerCommit の「serverSeq <= 水位」分岐で stale 扱いになり
 * optimistic/transient だけ外れて一時巻き戻る）。そこで、バッファは適用せず破棄し、水位 serverSeq・
 * committed・inFlightOps は据え置く（自分の未 ack op は inFlightOps に残るので楽観表示を維持する）。
 * そのうえで resyncRequestSeq を進め、呼び出し側（use-realtime-connection）に REST bootstrap での resync を
 * 能動的に促す。恒久欠番は権威スナップショットでしか正しく回復できないため、確定状態は resync で収束させる。
 * 水位を進めないので、破棄した op が後で遅着・再配信されても通常経路（seq > serverSeq なら連続/バッファ）で
 * 扱われ、stale 分岐での誤 ack は起きない。暴走防止の最終手段であり通常経路では使わない。
 */
function flushPendingCommits(state: CanvasDocState): { state: Partial<CanvasDocState> } {
  return {
    state: {
      // バッファを破棄してメモリを解放する（適用はしない＝水位・committed・inFlightOps は据え置き）。
      pendingCommits: {},
      // 権威スナップショットでの resync を呼び出し側へ要求する（確定状態は resync で収束させる）。
      resyncRequestSeq: state.resyncRequestSeq + 1,
      // resync 完了（loadSnapshot 全適用）まで、欠落 commit を含まない乖離状態をサムネイル・
      // 全体像 PDF の自動保存が永続化しないよう待機させる。
      resyncPending: true,
    },
  };
}

export const useCanvasDocStore = create<CanvasDocState>((set) => ({
  canvasId: null,
  serverSeq: 0,
  pendingCommits: {},
  resyncRequestSeq: 0,
  resyncPending: false,
  meta: DEFAULT_META,
  objects: {},
  committedMeta: DEFAULT_META,
  committedObjects: {},
  inFlightOps: [],
  attachmentLqips: {},
  exportSnapshotDirty: false,
  exportSnapshotDirtySeq: 0,

  loadSnapshot: ({ canvasId, serverSeq, objects, meta }) => {
    // このスナップショット取り込みで committed 化した pending op（snapshot に包含済み＋連続ドレイン分）。
    // 呼び出し側（use-realtime-connection）は、この op に対応する操作中ゴースト（transient）を破棄して、
    // 「順不同で先着してバッファ保留中だった op が snapshot 経由で確定したのにゴーストが残る」二重表示を防ぐ。
    let committedPendingOps: readonly CanvasOp[] = [];
    set((state) => {
      // 古い serverSeq の snapshot で適用済み commit を巻き戻さない（op.reject 後の再取得が古い snapshot を
      // 返す等）。同一 canvas に加え、初回ロード中（state.canvasId===null）も対象にする——onServerCommit は
      // canvasId を見ず seq のみで連続適用するため、canvasId=null のまま先着 op が committed へ畳み込まれ
      // 水位が進むことがある。ここで null を除外すると、遅れて届いた低い serverSeq の bootstrap で
      // その適用済み分が黙って巻き戻る（carry-over 側〔下記〕が null を許すのと非対称になる）。
      if (serverSeq < state.serverSeq) {
        if (state.canvasId === canvasId) {
          // resync 待ち中に届いた再取得がまだ stale（待機中に WebSocket の後続 commit で水位が先へ
          // 進んだ等）なら、resync をもう一度要求して権威スナップショットへの収束を継続する。
          // 再要求しないと resyncPending が残留してサムネイル・全体像 PDF の自動保存がセッション中
          // 停止し続けるうえ、flush（恒久欠番）経路では破棄済み commit が WebSocket から再配信されない
          // ため確定状態も収束しない。再要求は「新たな stale 応答を受けたとき」だけ発生するため、
          // タイマー等によらず応答駆動で有限に収束する。
          if (state.resyncPending) {
            return { resyncRequestSeq: state.resyncRequestSeq + 1 };
          }
          return state;
        }
        if (state.canvasId === null) {
          // stale な objects / serverSeq は適用しないが、canvasId だけは確定させる。null のまま残すと、
          // useCanvasThumbnail / useCanvasExportSnapshot が activeCanvasId !== canvasId の
          // 早期 return を続け、以後のサムネイル更新・全体像 PDF 自動保存が止まる。
          // あわせて resyncRequestSeq を進め、権威スナップショットへの resync を要求する。REST / MCP の
          // 直接書き込み（op 非経由・serverSeq が進まない）で事前投入されたキャンバスでは、null 中の
          // committed は「空ベースに op を当てた乖離状態」になり得る。canvasId 確定でサムネイル自動保存等が
          // 活性化する前に権威状態を取り直さないと、乖離した（ほぼ空の）内容がサーバへ永続化され得る。
          if (meta === undefined) {
            return {
              canvasId,
              resyncRequestSeq: state.resyncRequestSeq + 1,
              resyncPending: true,
            };
          }
          // meta も snapshot でしか得られないフィールド（createdAt / isFavorite / exportKey /
          // thumbnailUrl 等）は取り込んで確定させる。捨てると、この初回レースではタイトルが既定の
          // 「無題」のまま・お気に入り状態も未設定のまま残る（op では回復できない）。ただし
          // canvas.meta.update op で更新され得る title / thumbnailKey / updatedAt は、null 中に
          // 先着 op が既に進めた値を stale snapshot（op 未包含）で巻き戻さないよう現在値を優先する。
          // なお title の「op が進めたか」は既定値との比較のため、「既定タイトルへの改名 op」だけは
          // 『op 未適用』と区別できず snapshot 側の旧タイトルを一時採用する。これは thumbnailKey のみが
          // op で進んだケースで実タイトルを失わないことを優先した許容トレードオフで、上記の resync 要求に
          // よる権威スナップショット再取得で直ちに収束する。
          const mergedMeta: CanvasMetaState = {
            ...meta,
            ...(state.committedMeta.title !== DEFAULT_META.title
              ? { title: state.committedMeta.title }
              : {}),
            ...(state.committedMeta.thumbnailKey !== undefined
              ? { thumbnailKey: state.committedMeta.thumbnailKey }
              : {}),
            ...(state.committedMeta.updatedAt !== DEFAULT_META.updatedAt
              ? { updatedAt: state.committedMeta.updatedAt }
              : {}),
          };
          return {
            canvasId,
            meta: mergedMeta,
            committedMeta: mergedMeta,
            resyncRequestSeq: state.resyncRequestSeq + 1,
            resyncPending: true,
          };
        }
        // state.canvasId が別の非 null 値（別 canvas への切替）は stale 扱いせず、下で snapshot を適用する。
      }
      const base = {
        meta: meta ?? (state.canvasId === canvasId ? state.meta : DEFAULT_META),
        objects: Object.fromEntries(objects.map((o) => [o.id, o])),
      };
      let inFlightOps = state.canvasId === canvasId ? state.inFlightOps : [];
      // LQIP キャッシュは canvas 切替時のみクリアし、同一 canvas の再取得時は維持する。
      // snapshot に含まれる pending 添付（src がまだ data: URL の create のみ通った状態など）から
      // キャプチャしておく（in-flight / ドレイン分は drainContiguousPending が併せて拾う）。
      const baseLqips = state.canvasId === canvasId ? state.attachmentLqips : {};
      const baseAttachmentLqips = captureLqipsFromObjects(objects, baseLqips);
      // このスナップショットは serverSeq までの全 op を反映済みなので、連続水位をここへ張り直す。
      // 一方、順不同で先着してバッファ中の op のうち serverSeq より後（＝スナップショット未包含）のものは
      // 破棄せず保持し、連続する分はドレインする。これをしないと「バッファ中に、より低い serverSeq の
      // 再取得スナップショット（再接続 rebootstrap / op.reject・resync 後の fetch）が返ったとき」に、既知の
      // 確定を取りこぼす（他者 op は更新欠落、自 op は ack が外れず残留）。別キャンバス切替ではバッファも捨てる。
      const keptPending: Record<number, CanvasOp> = {};
      // スナップショットに包含済み（seq <= serverSeq）で破棄する pending op のうち、自分の楽観 op は
      // このスナップショットが ack 相当なので inFlightOps からも取り除く（残すと export ゲートが解除されず、
      // 増分 op では committed への二重反映リスクも残る）。
      const snapshotAckedOps: CanvasOp[] = [];
      // 初回 bootstrap（state.canvasId === null）でも、先着してバッファ中の pending は同一 canvas 宛て
      // （CanvasEditorView が同時起動する useRealtimeConnection(canvasId) が積んだもの）なので保持・ドレイン
      // する。別 canvas への切替（state.canvasId が別の非 null id）のときだけ pending を捨てる。これをしないと、
      // 初回 fetch 完了前に WebSocket で先着した snapshot 未包含（seq > serverSeq）の確定を「別 canvas 扱い」で
      // 取りこぼす（本フックが useBootstrap と realtime を同時起動する初回レース）。
      if (state.canvasId === canvasId || state.canvasId === null) {
        for (const [seqKey, pendingOp] of Object.entries(state.pendingCommits)) {
          if (Number(seqKey) > serverSeq) {
            keptPending[Number(seqKey)] = pendingOp;
          } else {
            snapshotAckedOps.push(pendingOp);
          }
        }
      }
      if (snapshotAckedOps.length > 0) {
        const ackedClientOpIds = new Set(snapshotAckedOps.map((p) => p.clientOpId));
        inFlightOps = inFlightOps.filter((p) => !ackedClientOpIds.has(p.clientOpId));
      }
      // snapshot の objects に同一 id のオブジェクトが含まれる自分の in-flight 作成 op
      // （object.create / pen.stroke.commit）も、この snapshot が ack 相当（作成は確定済み）なので
      // inFlightOps から取り除く。op.commit の配信欠落（切断・broadcast 失敗）後に再接続
      // rebootstrap / resync で水位（serverSeq）だけが確定分を越えると、これらの op は通常の ack
      // （applyContiguousCommit）でも上記 snapshotAckedOps（pendingCommits 由来）でも除去されずに
      // 永久残留し、以後の再計算（committed + inFlightOps）で確定済みオブジェクトを作成時点の状態で
      // 上書きし続ける（applyOpToObjects 側の冪等化ガードと合わせた二段防御）。残留はさらに
      // export 自動保存ゲート（inFlightOpsAffectExport）を閉じ続ける二次障害も起こすため、
      // 表示破壊の防御だけでなく残留自体をここで解消する。作成 op 以外（transform 等）は
      // snapshot 包含の判定を id 存在では行えないため対象にしない（従来どおり ack / reject 待ち）。
      const snapshotCoveredCreates = inFlightOps.filter(
        (p, index) =>
          (p.type === "object.create" || p.type === "pen.stroke.commit") &&
          base.objects[p.object.id] !== undefined &&
          // 同一オブジェクトの object.delete がこの create より前に in-flight で残っている場合は
          // 回収しない。「committed 済みオブジェクトを削除 → undo で同一 id を再作成」の途中に
          // snapshot が届いたケースでは、snapshot 内の同一 id は『再作成 create の確定』ではなく
          // 『delete 未適用の旧オブジェクト』であり得るため（誤回収すると表示再計算が delete
          // だけを再適用し、undo したはずのオブジェクトが消えて見える）。この create は従来どおり
          // ack / reject / 後続 snapshot で収束させる。
          !inFlightOps.some(
            (q, qIndex) =>
              qIndex < index && q.type === "object.delete" && q.objectId === p.object.id
          )
      );
      if (snapshotCoveredCreates.length > 0) {
        const coveredClientOpIds = new Set(snapshotCoveredCreates.map((p) => p.clientOpId));
        inFlightOps = inFlightOps.filter((p) => !coveredClientOpIds.has(p.clientOpId));
      }
      const drained = drainContiguousPending({
        committedMeta: base.meta,
        committedObjects: base.objects,
        inFlightOps,
        attachmentLqips: baseAttachmentLqips,
        fromSeq: serverSeq,
        pendingCommits: keptPending,
      });
      // 表示用は「サーバ確定（ドレイン後）＋残りの in-flight」。残りの in-flight から LQIP も拾う。
      let attachmentLqips = drained.attachmentLqips;
      for (const op of drained.inFlightOps) {
        attachmentLqips = nextAttachmentLqips(op, attachmentLqips);
      }
      const next = drained.inFlightOps.reduce(applyOpToState, {
        meta: drained.committedMeta,
        objects: drained.committedObjects,
      });
      // snapshot 包含済み分（snapshotAckedOps / snapshotCoveredCreates）と連続ドレイン分
      // （drained.appliedOps）は、この取り込みで committed へ反映された。呼び出し側で
      // これらの transient ゴーストを破棄する（作成 op のゴースト破棄は id 単位で冪等）。
      committedPendingOps = [...snapshotAckedOps, ...snapshotCoveredCreates, ...drained.appliedOps];
      return {
        canvasId,
        serverSeq: drained.appliedSeq,
        pendingCommits: drained.pendingCommits,
        committedMeta: drained.committedMeta,
        committedObjects: drained.committedObjects,
        meta: next.meta,
        objects: next.objects,
        inFlightOps: drained.inFlightOps,
        attachmentLqips,
        // 別キャンバスへの切り替え（初回 bootstrap）はローカル編集が無いため dirty を下げる。一方、同一キャンバスの
        // 再取得（再接続・resync・bootstrap.snapshot）では既存 dirty を維持する。ここで inFlightOps.length>0 で
        // 再計算すると、「ローカル編集 → ack 到着で inFlightOps=[] → export 前に reconnect/resync」の順で dirty が
        // false に戻り、このタブの未 export なローカル編集が保存されなくなる（ack 済みでも export はまだ）。受信
        // 専用タブは applyOptimistic を呼ばず dirty=false のままなので、同一キャンバス維持でも観測者は保存しない。
        exportSnapshotDirty: state.canvasId === canvasId ? state.exportSnapshotDirty : false,
        // 権威スナップショットを取り込めたので、初回 stale 無視による resync 待ちを解除する
        // （サムネイル・全体像 PDF の自動保存が権威状態ベースで再開できる）。
        resyncPending: false,
      };
    });
    return committedPendingOps;
  },

  updateLocalMeta: (meta) =>
    set((state) => ({
      meta: {
        ...state.meta,
        ...meta,
      },
      committedMeta: {
        ...state.committedMeta,
        ...meta,
      },
    })),

  applyOptimistic: (op) =>
    set((state) => {
      const next = applyOpToState({ meta: state.meta, objects: state.objects }, op);
      // 全体像 PDF に描画される drawable を変えるローカル編集か。コメント（comment-thread は非描画）や
      // タイトル/サムネ等のメタ更新は PDF 内容に影響しないため、これらだけを触ったタブが export を再保存
      // （低品質 PDF への劣化上書き）しないようにする。
      const affects = opAffectsExportSnapshot(op, state.objects);
      return {
        inFlightOps: [...state.inFlightOps, op],
        meta: next.meta,
        objects: next.objects,
        attachmentLqips: nextAttachmentLqips(op, state.attachmentLqips),
        // 非対象 op は既存 dirty を維持。
        exportSnapshotDirty: state.exportSnapshotDirty || affects,
        // export 影響編集ごとに seq を単調増加させる。保存フックが保存中の追加編集を検知して取りこぼさない
        // ようにするためのカウンタ（finalize 後に保存開始時点と比較する）。非対象 op では進めない。
        exportSnapshotDirtySeq: state.exportSnapshotDirtySeq + (affects ? 1 : 0),
      };
    }),

  onServerCommit: (op, serverSeq) => {
    // 今回 committed へ実際に畳み込んだ（＝効果が committed に存在する）op 群。呼び出し側はこの op に
    // 対してのみ transient ゴーストを破棄する。順不同で先着してバッファ保留しただけの op は含めない。
    let appliedOps: readonly CanvasOp[] = [];
    set((state) => {
      // 連続水位以下（スナップショット反映済み、または既に適用済みの seq の重複配信）。committed へは
      // 再適用しないが、その効果は committed に存在するため transient ゴーストは破棄対象にする。
      // 自分が送った op の ack なら inFlightOps からも取り除いて表示を committed の確定値へ収束させる。
      if (serverSeq <= state.serverSeq) {
        appliedOps = [op];
        const remaining = state.inFlightOps.filter((p) => p.clientOpId !== op.clientOpId);
        if (remaining.length === state.inFlightOps.length) return state;
        const next = remaining.reduce(applyOpToState, {
          meta: state.committedMeta,
          objects: state.committedObjects,
        });
        return { meta: next.meta, objects: next.objects, inFlightOps: remaining };
      }

      // 間の seq が未着（順不同で先に届いた）。連続化のためバッファへ積み、確定は保留する。まだ committed へ
      // 反映していないため appliedOps には含めない（transient ゴーストは連続到着でドレインされるまで残す）。
      // 自分の未 ack op はここでは inFlightOps に残したまま（楽観表示を維持し巻き戻さない）。
      if (serverSeq > state.serverSeq + 1) {
        if (state.pendingCommits[serverSeq] !== undefined) return state; // 重複配信は無視（未適用）
        const pendingCommits = { ...state.pendingCommits, [serverSeq]: op };
        // 恒久的な欠番でバッファが膨らみ続けるのを防ぐ最終手段（通常経路では到達しない）。バッファを破棄し
        // 水位を据え置いたまま resync を要求する（appliedOps は空のまま＝何も committed 化しない。確定は
        // resync スナップショットで収束させる）。
        if (Object.keys(pendingCommits).length > PENDING_COMMITS_FLUSH_THRESHOLD) {
          return flushPendingCommits(state).state;
        }
        return { pendingCommits };
      }

      // serverSeq === state.serverSeq + 1: 連続。committed へ畳み込み、後続のバッファを連続ドレインする。
      // applyOpToObjects を serverSeq 昇順で適用するため、同一オブジェクトへの部分更新・増分更新も
      // 順不同到着で取りこぼさず、最終状態がサーバと一致する。
      const result = applyContiguousCommit(state, op);
      appliedOps = result.appliedOps;
      return result.state;
    });
    return appliedOps;
  },

  onServerReject: (clientOpId) =>
    set((state) => {
      const target = state.inFlightOps.find((p) => p.clientOpId === clientOpId);
      if (!target) return state;
      const remaining = state.inFlightOps.filter((p) => p.clientOpId !== clientOpId);
      // 拒否された op だけを除外し、サーバ確定スナップショットへ残りの楽観 op を再適用する。
      const next = remaining.reduce(applyOpToState, {
        meta: state.committedMeta,
        objects: state.committedObjects,
      });
      // attachmentLqips は再計算しない（Zustand の merge で既存値が保持される）。
      // 楽観 object.delete が reject されてもオブジェクト本体は復元されるが、LQIP は
      // delete でプルーンしない方針（nextAttachmentLqips 参照）のため、reject 後も
      // そのままフォールバックとして使い続けられる。
      return {
        meta: next.meta,
        objects: next.objects,
        inFlightOps: remaining,
      };
    }),

  requestGapResync: () =>
    set((state) => {
      if (Object.keys(state.pendingCommits).length === 0) return state;
      // 欠番上限到達時（onServerCommit 内）と同じ回収経路に乗せる:
      // バッファ破棄＋resyncRequestSeq increment＋resync 完了までの保存待機（resyncPending）。
      return flushPendingCommits(state).state;
    }),

  removeInFlightOp: (clientOpId) =>
    set((state) => {
      const remaining = state.inFlightOps.filter((p) => p.clientOpId !== clientOpId);
      if (remaining.length === state.inFlightOps.length) return state;
      // 残りの楽観 op をサーバ確定スナップショットへ再適用し、対象 op の効果だけを取り除く。
      const next = remaining.reduce(applyOpToState, {
        meta: state.committedMeta,
        objects: state.committedObjects,
      });
      return {
        meta: next.meta,
        objects: next.objects,
        inFlightOps: remaining,
      };
    }),

  clearExportSnapshotDirty: () => set({ exportSnapshotDirty: false }),

  reset: () =>
    set({
      canvasId: null,
      serverSeq: 0,
      pendingCommits: {},
      resyncRequestSeq: 0,
      resyncPending: false,
      meta: DEFAULT_META,
      objects: {},
      committedMeta: DEFAULT_META,
      committedObjects: {},
      inFlightOps: [],
      attachmentLqips: {},
      exportSnapshotDirty: false,
      exportSnapshotDirtySeq: 0,
    }),
}));

/**
 * この op が全体像 PDF（export スナップショット）に描画される drawable を変えるかを返す。
 * drawable 判定は共有正本（@refinement-canvas/domain の isDrawableObject）を使い、comment-thread のみ非描画。
 * - object.create: 作成オブジェクトが drawable なら影響。pen.stroke.commit は常に drawable。
 * - 既存オブジェクトの変更/削除（transform / text.update / style.update / delete）は、対象オブジェクトが
 *   drawable なら影響（comment-thread の移動・削除は PDF に影響しない）。
 * - object.attachment.update は src で分岐する。src=blob: は本人用ローカルプレビュー（送信されず、外部配信
 *   スナップショットの描画でも除外される＝placeholder 扱い）なので dirty 化しない。これで dirty 化すると、
 *   既存画像の差し替え途中（blob プレビューだけの状態）で保存が走り、外部 PDF が旧確定画像ではなく placeholder
 *   へ劣化する。公開 URL 等の非 blob（finalize 済み）への差し替えだけ dirty 化する。inFlightOpsAffectExport・
 *   描画側（excludeUnpublishedSrc）の blob 除外と整合させ、サーバ未公開のローカル表示を current に混ぜない。
 * - comment.message.* / canvas.meta.update は PDF 内容に影響しない。
 * これにより、コメント/メタだけを触ったタブが export を再保存して劣化上書きするのを防ぐ。
 */
function opAffectsExportSnapshot(op: CanvasOp, objects: Record<string, CanvasObject>): boolean {
  switch (op.type) {
    case "object.create":
      return isDrawableObject(op.object.type);
    case "pen.stroke.commit":
      return true;
    case "object.attachment.update":
      // blob: の本人用プレビューは dirty 化しない（差し替え途中の placeholder 劣化保存を防ぐ）。
      return !op.src.startsWith("blob:") && isDrawableObject(objects[op.objectId]?.type);
    case "object.delete":
    case "object.transform":
    case "object.text.update":
    case "object.style.update":
      return isDrawableObject(objects[op.objectId]?.type);
    case "comment.message.add":
    case "comment.message.update":
    case "comment.message.delete":
    case "canvas.meta.update":
      return false;
    default: {
      // 網羅性ガード。CanvasOp に drawable を変える新 op が増えたとき、ここで型エラーになって追従漏れ
      // （default で黙って false を返し、その編集が export スナップショットへ反映されない）を検知する。
      const exhaustiveCheck: never = op;
      void exhaustiveCheck;
      return false;
    }
  }
}

/**
 * 未 ack の op 群（inFlightOps）の中に、全体像 PDF（export スナップショット）へ影響し得る drawable 編集が
 * あるかを返す。外部配信用 PDF を「楽観状態のまま確定」しないため、export 自動保存はこれが false の
 * （= export に影響する op がすべて ack / reject 済みの）ときだけ行う。これにより「楽観作成 → 20 秒後に
 * export 保存 → その後サーバ reject」の順で、本体に存在しないオブジェクトを含む PDF が外部配信用に残るのを防ぐ。
 *
 * delete / transform / text.update / style.update は、対象オブジェクトの drawable 判定が in-flight 時点
 * （楽観適用後で対象が既に消えている等）では確実に行えないため、安全側（影響あり）に倒す。これらは必ず
 * サーバへ送信され ack / reject されるため、確定を待っても短時間で解ける。object.create は作成オブジェクト
 * 自身の型で判定でき、comment.* / canvas.meta.update は PDF 内容に影響しない。
 *
 * object.attachment.update は src で分岐する。createAttachmentPlaceholder が作る「本人用ローカル上書き」は
 * src=blob: の送信しない op で、ack されず inFlightOps に残り続ける（公開 URL が無い環境では finalize / discard も
 * 呼ばれず永久に残る）。これをゲート対象にすると export 自動保存が永久に保存できなくなる一方、blob preview は
 * 本人だけのローカル表示でサーバ確定を待つ意味が無い。よって blob: src は影響なし扱いにして除外し、公開 URL 等の
 * 非 blob src への差し替え（サーバ送信される本物の編集）は ack / reject 前に確定しないよう影響あり扱いにする。
 * これにより、サーバ ack が必要な op はゲートで待ち（未 ack の楽観状態を外部 PDF として確定しない）、永続化され
 * ない blob preview だけを除外できる（待ちの打ち切り上限は設けない＝上限到達後に未確定状態を保存しない）。
 */
export function inFlightOpsAffectExport(ops: readonly CanvasOp[]): boolean {
  // 判定は共有正本（@refinement-canvas/domain の opMayAffectExportSnapshot）に集約する。api の finalize 世代ゲート
  // （描画後に export 影響 op が確定したか）と同義になるよう同じ述語を使う。blob: の本人用 preview 除外もこの中。
  return ops.some(opMayAffectExportSnapshot);
}

/**
 * 単一 op に応じて attachmentLqips の次状態を返す。
 * - 画像・動画の object.create で src が data: URL（透明プレースホルダ以外）なら、LQIP として登録する。
 * - object.delete では LQIP をプルーンしない（session 寿命のキャッシュ）。プルーンすると、
 *   楽観削除 → サーバ reject でオブジェクト本体が復元されたときに、復活した画像の
 *   ぼかしフォールバックだけが失われる退行が起きるため。1 件最大 8KB の小さなキャッシュであり、
 *   canvas 切替・reset 時にはクリアされるので、session 寿命の保持で問題ない。
 * それ以外の op は LQIP キャッシュに影響しない。
 */
function nextAttachmentLqips(
  op: CanvasOp,
  current: Record<string, string>
): Record<string, string> {
  if (op.type === "object.create") {
    const created = op.object;
    if (
      (created.type === "image" || created.type === "video") &&
      isPendingAttachmentSrc(created.src) &&
      created.src !== PENDING_ATTACHMENT_SRC &&
      current[created.id] !== created.src
    ) {
      return { ...current, [created.id]: created.src };
    }
  }
  return current;
}

/**
 * snapshot で受け取ったオブジェクト群から LQIP をキャプチャする。
 * 同一 canvas の再取得時に既存キャッシュを引き継ぎつつ、pending 状態の添付があれば追加する。
 */
function captureLqipsFromObjects(
  objects: readonly CanvasObject[],
  current: Record<string, string>
): Record<string, string> {
  let next = current;
  for (const obj of objects) {
    if (obj.type !== "image" && obj.type !== "video") continue;
    if (!isPendingAttachmentSrc(obj.src)) continue;
    if (obj.src === PENDING_ATTACHMENT_SRC) continue;
    if (next[obj.id] === obj.src) continue;
    next = { ...next, [obj.id]: obj.src };
  }
  return next;
}

function applyOpToState(
  state: { readonly meta: CanvasMetaState; readonly objects: Record<string, CanvasObject> },
  op: CanvasOp
): { readonly meta: CanvasMetaState; readonly objects: Record<string, CanvasObject> } {
  return {
    meta: applyOpToMeta(state.meta, op),
    objects: applyOpToObjects(state.objects, op),
  };
}

function applyOpToMeta(meta: CanvasMetaState, op: CanvasOp): CanvasMetaState {
  if (op.type !== "canvas.meta.update") return meta;
  return {
    ...meta,
    ...(op.title !== undefined ? { title: op.title, updatedAt: op.clientCreatedAt } : {}),
    ...(op.thumbnailKey !== undefined
      ? { thumbnailKey: op.thumbnailKey, updatedAt: op.clientCreatedAt }
      : {}),
    ...(op.sizePreset !== undefined
      ? { sizePreset: op.sizePreset, updatedAt: op.clientCreatedAt }
      : {}),
  };
}

/**
 * 単一 op を objects 連想配列に適用する。
 * サーバ権威モデルでは、同じ関数を server / client の両方で適用することで決定論的に状態を再現する。
 */
function applyOpToObjects(
  objects: Record<string, CanvasObject>,
  op: CanvasOp
): Record<string, CanvasObject> {
  switch (op.type) {
    case "object.create":
      // 同一 id のオブジェクトが既に存在する場合は上書きせず現状維持する（冪等化）。
      // オブジェクト id はクライアント生成の一意値であり、存在する時点でこの create は確定済み
      // （サーバ op-applier も重複 create を reject する）。op.commit の配信欠落後に再接続
      // rebootstrap / resync で確定した create が inFlightOps へ残留すると、以後の再計算
      // （committed + inFlightOps の再適用）のたびに、返信・削除が反映済みの最新オブジェクトを
      // 「作成時点の状態」で上書きし続ける（コメントスレッドで削除したはずの起点コメントが
      // ピンのプレビューへ復活する実障害）。このガードでその上書き破壊を構造的に防ぐ
      // （楽観適用時は id 未存在のため通常動作へ影響しない）。
      if (objects[op.object.id] !== undefined) return objects;
      return { ...objects, [op.object.id]: op.object };
    case "object.delete": {
      const next = { ...objects };
      delete next[op.objectId];
      return next;
    }
    case "object.transform": {
      const current = objects[op.objectId];
      if (!current) return objects;
      const transformed = applyObjectTransform(current, op.transform);
      return {
        ...objects,
        [op.objectId]: applyStickyAuthorshipIfRelevant(transformed, op.actorDisplayName),
      };
    }
    case "object.text.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      const next = { ...current, text: op.text } as CanvasObject;
      return {
        ...objects,
        [op.objectId]: applyStickyAuthorshipIfRelevant(next, op.actorDisplayName),
      };
    }
    case "object.style.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      const merged = mergeObjectStyle(current, op.style);
      if (!merged) return objects;
      return {
        ...objects,
        [op.objectId]: applyStickyAuthorshipIfRelevant(merged, op.actorDisplayName),
      };
    }
    case "pen.stroke.commit":
      // object.create と同じ理由で冪等化する（確定済みストロークの残留 op による上書きを防ぐ）。
      if (objects[op.object.id] !== undefined) return objects;
      return { ...objects, [op.object.id]: op.object };
    case "comment.message.add": {
      // サーバ op-applier と同じ純粋ロジックで決定論的に適用する（楽観・サーバ・他参加者で一致）。
      const current = objects[op.objectId];
      if (!current || current.type !== "comment-thread") return objects;
      const next = applyCommentAdd(current, op.message, {
        at: op.clientCreatedAt,
        by: op.actorUserId,
      });
      return { ...objects, [op.objectId]: next };
    }
    case "comment.message.update": {
      const current = objects[op.objectId];
      if (!current || current.type !== "comment-thread") return objects;
      const next = applyCommentUpdate(current, op.messageId, op.body, {
        at: op.clientCreatedAt,
        by: op.actorUserId,
      });
      return { ...objects, [op.objectId]: next };
    }
    case "comment.message.delete": {
      const current = objects[op.objectId];
      if (!current || current.type !== "comment-thread") return objects;
      const next = applyCommentDelete(current, op.messageId, {
        at: op.clientCreatedAt,
        by: op.actorUserId,
      });
      // 最後のメッセージ削除でスレッドが空になったら、オブジェクトごと取り除く（サーバ側と対称）。
      if (next === null) {
        const rest = { ...objects };
        delete rest[op.objectId];
        return rest;
      }
      return { ...objects, [op.objectId]: next };
    }
    case "object.attachment.update": {
      const current = objects[op.objectId];
      if (!current) return objects;
      // image / video / link-card に共通する src/imageUrl 系のフィールドを更新する。
      if (current.type === "image" || current.type === "video") {
        return {
          ...objects,
          [op.objectId]: { ...current, src: op.src } as CanvasObject,
        };
      }
      if (current.type === "link-card") {
        return {
          ...objects,
          [op.objectId]: { ...current, imageUrl: op.src } as CanvasObject,
        };
      }
      return objects;
    }
    case "canvas.meta.update":
      return objects;
    default:
      return objects;
  }
}

/**
 * 付箋の場合のみ、op.actorDisplayName を updatedByName へ反映する。
 * 楽観反映でもサーバ反映でも同じ振る舞いを再現するため、サーバ側 op-applier と対称な実装にする。
 */
function applyStickyAuthorshipIfRelevant(
  object: CanvasObject,
  actorDisplayName: string | undefined
): CanvasObject {
  if (object.type !== "sticky") return object;
  if (typeof actorDisplayName !== "string" || actorDisplayName.length === 0) return object;
  if (object.updatedByName === actorDisplayName) return object;
  return { ...object, updatedByName: actorDisplayName };
}

/**
 * `object.style.update` の style パッチを、対応するフィールドを持つオブジェクト種別にだけ反映する。
 * 対象外オブジェクトは null を返し、呼び出し側で無視する。
 */
function mergeObjectStyle(
  current: CanvasObject,
  style: Partial<{
    color: string;
    fontSize: number;
    fill: string;
    stroke: string;
    textAlign: "left" | "center" | "right";
  }>
): CanvasObject | null {
  switch (current.type) {
    case "sticky": {
      // 付箋は color と textAlign を更新しうる。どちらの指定も無ければ現状維持。
      if (style.color === undefined && style.textAlign === undefined) return current;
      const next = { ...current };
      if (style.color !== undefined) next.color = style.color;
      if (style.textAlign !== undefined) next.textAlign = style.textAlign;
      return next;
    }
    case "text": {
      const next = { ...current };
      if (style.color !== undefined) next.color = style.color;
      if (style.fontSize !== undefined) next.fontSize = style.fontSize;
      if (style.textAlign !== undefined) next.textAlign = style.textAlign;
      return next;
    }
    case "shape": {
      const next = { ...current };
      if (style.fill !== undefined) next.fill = style.fill;
      if (style.stroke !== undefined) next.stroke = style.stroke;
      return next;
    }
    case "section": {
      // セクションは背景色（fill）のみ変更できる（サーバ op-applier / REST と同一契約）。
      if (style.fill === undefined) return current;
      return { ...current, fill: style.fill };
    }
    case "pen-stroke": {
      if (style.color === undefined) return current;
      return { ...current, color: style.color };
    }
    default:
      return null;
  }
}

function applyObjectTransform(
  current: CanvasObject,
  transform: TransformObjectOp["transform"]
): CanvasObject {
  if (current.type !== "pen-stroke") {
    return { ...current, ...transform } as CanvasObject;
  }

  const nextX = transform.x ?? current.x;
  const nextY = transform.y ?? current.y;
  const dx = nextX - current.x;
  const dy = nextY - current.y;

  // ペンストロークの points は stage 座標で保持しているため、
  // オブジェクト移動時は外接矩形と同じ差分だけポイント群も移動する。
  return {
    ...current,
    ...transform,
    points:
      dx === 0 && dy === 0
        ? current.points
        : current.points.map((point) => ({ ...point, x: point.x + dx, y: point.y + dy })),
  };
}
